import React, { useRef } from "react";
import {
  Card,
  CardActionArea,
  CardMedia,
  ClickAwayListener,
  Skeleton,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
  Box,
  CircularProgress,
} from "@mui/material";
import { useContentStatus } from "../hooks/useCustomQuery";
import { useThemeContext } from "../context/themeContext";
import PlaceholderPoster from "./PlaceholderPoster";
import { TrackIcon } from "./icons.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useApi from "../hooks/useApi";
import {
  applyStatusClearToCache,
  applyStatusWriteToCache,
} from "../utils/cacheUtils";
import {
  getContentLabel,
  getContentOptions,
  getStatusOptionsArray,
  handleApiError,
} from "../utils/utils";
import { useAuth } from "../context/authContext";
import ConfirmActionDialog from "./ConfirmActionDialog";
import { ConditionalWrapper } from "./ConditionalWraper";
import { toast } from "react-toastify";

/**
 * PosterTile
 *
 * Displays a content poster (film, TV, book) as a card with optional
 * status tracking and list action overlays.
 *
 * INTERACTION MODEL
 * -----------------
 * Desktop:
 *   - Hover reveals the status overlay or list actions panel
 *   - Click calls the onClick prop (navigation)
 *
 * Touch:
 *   - Tap toggles the status overlay or list actions panel
 *   - Long press (500ms) calls the onClick prop (navigation)
 *
 * TOUCH HANDLING NOTES
 * --------------------
 * Touch interactions are handled entirely via onTouchStart/onTouchEnd,
 * NOT via onClick. This is intentional — on touch devices the browser
 * fires a synthetic click ~300ms after a touch sequence ends, which
 * causes race conditions when overlays are involved. By setting onClick
 * to undefined on touch devices and handling everything in the touch
 * events, we avoid this entirely.
 *
 * Interactive child elements (status buttons, list action buttons) must
 * also follow this pattern:
 *   - onTouchEnd: stopPropagation + preventDefault + call action
 *   - onClick: stopPropagation + call action (desktop fallback)
 *
 * stopPropagation prevents touch/click events from bubbling up to the
 * Wrapper and triggering its handlers unintentionally.
 * preventDefault on onTouchEnd suppresses the synthetic click so the
 * onClick handler does not fire a second time on touch devices.
 *
 * @param {string}   contentId      - Unique identifier for the content item
 * @param {string}   contentType    - Type of content: "movie" | "tv" | "book"
 * @param {string}   contentTitle   - Title used for alt text and placeholder poster
 * @param {string}   imageUrl       - Poster image path (TMDB or ISBN)
 * @param {function} onClick        - Navigation handler, called on click (desktop) or long press (touch)
 * @param {"full"|"short"|null} showStatus - Status overlay mode. "full" shows
 *                                           all tracking buttons (queue/start/
 *                                           stop/finish); "short" shows only
 *                                           the queue and finish buttons; null
 *                                           hides the overlay entirely.
 * @param {Array}    listActions    - Additional action buttons to show in the options panel (e.g. remove from list)
 * @param {number}   position       - Optional ranking position number rendered over the poster
 * @param {boolean}  showType       - Optional content type icon rendered over the poster's
 *                                    top-left corner. Shares that corner with `position`,
 *                                    so set one or the other (position wins if both are set)
 * @param {string}   padding        - Optional padding, removes box shadow when set
 * @param {number}   zIndex         - Optional z-index override for the card
 * @param {boolean}  skeleton       - If true, renders a static skeleton placeholder
 * @param {boolean}  inline         - If true, renders as a Box instead of a Card
 * @param {string}   overlay        - If true, overlay applied to PlaceholderPoster when no image is available
 */
function PosterTile({
  contentId,
  contentType,
  contentTitle,
  imageUrl,
  onClick,
  showStatus = null,
  listActions = [],
  position = null,
  showType = false,
  padding,
  zIndex,
  skeleton = false,
  inline = false,
  overlay = false,
}) {
  const { user, openAuthDialog } = useAuth();
  const { addStatus, deleteStatus } = useApi();

  const queryClient = useQueryClient();
  const theme = useTheme();
  const { isTouchDevice } = useThemeContext();

  // Custom touch handling is only needed when tap and long press do different things.
  // When neither showStatus nor listActions is configured, both gestures call onClick,
  // so we fall back to native onClick and skip the custom touch layer entirely.
  const needsCustomTouchHandling =
    isTouchDevice && (showStatus || listActions.length > 0);

  // Controls visibility of the list actions panel (shown when listActions is non-empty)
  const [showOptions, setShowOptions] = React.useState(false);

  // Controls visibility of the status tracking overlay
  const [showStatusOverlay, setShowStatusOverlay] = React.useState(false);

  // Your tracking status for this item (signed in only): always with
  // showStatus, for its border and overlay; otherwise fetched once the options
  // panel opens, so the status overlay its Track button opens already shows
  // where you are. Batched and cached per item, so this stays cheap.
  const { data: userStatus } = useContentStatus(contentId, {
    enabled: !!user && (!!showStatus || showOptions || showStatusOverlay),
  });

  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [imageFailed, setImageFailed] = React.useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = React.useState(false);
  // Whether the pending delete confirmation is a series-wide clear (cascades to
  // every season's status and journal entries) vs a single-item one, so the
  // dialog can warn accurately.
  const [deleteConfirmSeriesWide, setDeleteConfirmSeriesWide] =
    React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  // True for the brief window between the long-press threshold firing and the
  // finger lifting. Drives a one-shot "pop" on the card so the user gets a felt
  // signal that the long press registered (navigation now opens on lift, so the
  // popping modal no longer doubles as that cue).
  const [longPressArmed, setLongPressArmed] = React.useState(false);

  // True while the card pops to confirm a status set from this tile, so the
  // change lands as a felt beat rather than only a border swap. Cleared when
  // the pop ends, so the next status change can replay it.
  const [statusPop, setStatusPop] = React.useState(false);

  // Used to implement long-press detection on touch devices.
  // If the timer fires, it was a long press. If onTouchEnd clears it first, it was a tap.
  const timerRef = useRef(null);
  // Records the touch start position so handleTouchMove can ignore natural finger
  // drift and only cancel the gesture on intentional scroll movement.
  const touchStartPos = useRef({ x: 0, y: 0 });

  // "short" mode only exposes queue and finish; "full" mode exposes everything
  // except the synthetic "none" option used by the picker dropdown.
  const shortStatusValues = ["queued", "finished"];
  const statusOptions = getStatusOptionsArray(theme).filter((option) => {
    if (option.value === "none") return false;
    if (showStatus === "short") return shortStatusValues.includes(option.value);
    return true;
  });
  // Merge external list actions with the built-in "Track" action.
  // The Track button opens the status overlay from within the list actions panel.
  // Always the app's Track icon — your current status shows in the overlay.
  const secondaryActions = [
    ...listActions,
    {
      label: "Track",
      value: "change_status",
      icon: <TrackIcon fontSize="inherit" />,
      onClick: () => {
        setShowStatusOverlay(true);
      },
    },
  ];

  // ─── Touch Handlers (Wrapper) ─────────────────────────────────────────────
  //
  // These handle the top-level tap vs long-press distinction on the card itself.
  // See component JSDoc for full explanation of why touch and click are separated.

  const handleTouchStart = (e) => {
    // Record start position for drift detection in handleTouchMove.
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };
    // Start the long-press timer. If it fires before onTouchEnd clears it, the
    // interaction is treated as a long press. We only FLAG the long press here
    // (sentinel "fired", not null, so handleTouchEnd can tell it apart from a
    // touchMove cancellation). Navigation is invoked in handleTouchEnd on finger
    // lift — NOT from this timer. Opening the modal mid-gesture mounts it under
    // the finger, and the trailing synthetic click then leaks onto it in the iOS
    // standalone PWA (closing it when long-pressing near the bottom of a list).
    // Deferring to touchend makes long-press behave like the working tap branch.
    timerRef.current = setTimeout(() => {
      timerRef.current = "fired";
      setLongPressArmed(true); // trigger the "release to open" pop
    }, 500);
  };

  const handleTouchEnd = (e) => {
    setLongPressArmed(false); // gesture is over — reset the pop for the next press
    if (timerRef.current && timerRef.current !== "fired") {
      // Timer is still pending — the touch ended before 500ms, so treat as a tap.
      clearTimeout(timerRef.current);
      timerRef.current = null;

      // Suppress the synthetic click the browser fires ~300ms after touchEnd.
      // On Android (Blink), that click is re-hit-tested by coordinates at dispatch
      // time, so it lands on whichever overlay button this tap just rendered under
      // the finger — spuriously selecting a status. Safe to call here because, when
      // custom touch handling is active, the Wrapper's onClick is undefined and
      // navigation is invoked directly via onClick() below, not via the synthetic click.
      e.preventDefault();

      // Tap priority logic:
      // 1. If status overlay is open, close it (tap away to dismiss) — however
      //    it was opened, including from the list actions' Track button
      // 2. If list actions are present, toggle the options panel
      // 3. If status tracking is enabled, open the status overlay
      // 4. Fallback to onClick (navigation) if no overlays are configured
      if (showStatusOverlay) {
        setShowStatusOverlay(false);
      } else if (listActions.length > 0) {
        setShowOptions((prev) => !prev);
      } else if (showStatus) {
        setShowStatusOverlay(true);
      } else if (onClick) {
        onClick();
      }
    } else if (timerRef.current === "fired") {
      // Long press completed. Suppress the synthetic click the browser fires
      // ~300ms after touchEnd, THEN invoke navigation — same order as the tap
      // branch above. Opening the modal here (on finger lift) rather than from
      // the long-press timer keeps the DOM stable during the gesture, so the
      // click suppression actually takes effect and the ghost click can no longer
      // land on the freshly opened modal.
      e.preventDefault();
      timerRef.current = null;
      if (onClick) onClick();
    }
    // null means touchMove cancelled the gesture — do nothing and let any synthetic
    // click bubble naturally (e.g. Cover.js relies on this to open the collection).
  };

  const handleTouchMove = (e) => {
    // Ignore natural finger drift — only cancel if movement exceeds 10px.
    // Without this threshold, Android's small touch drift during a stationary
    // tap would cancel the gesture, causing intermittent tap failures.
    const touch = e.changedTouches[0];
    const dx = Math.abs(touch.clientX - touchStartPos.current.x);
    const dy = Math.abs(touch.clientY - touchStartPos.current.y);
    if (dx <= 10 && dy <= 10) return;

    // Genuine scroll — cancel any pending long-press and dismiss overlays.
    // Sets timerRef to null (not "fired") so handleTouchEnd knows this was a
    // cancelled gesture, not a completed long press.
    clearTimeout(timerRef.current);
    timerRef.current = null;
    setLongPressArmed(false); // scroll cancelled the gesture — reset the pop
    setShowOptions(false);
    setShowStatusOverlay(false);
  };

  // ─── Status Mutations ─────────────────────────────────────────────────────
  const addStatusMutation = useMutation({
    mutationFn: addStatus,
    onSuccess: (data) => {
      setSubmitting(false);
      setShowStatusOverlay(false);
      setShowOptions(false);
      if (showStatus) setStatusPop(true);
      // The tile's status border confirms the write, except when a multi-season
      // series' queue/start landed on a season — only then name where it went.
      if (data.seasonResolved) toast.success(data.message);
      // For a multi-season TV series tile, the backend may have resolved the
      // action onto a specific season rather than the tile's own (series) id
      // — see resolve_tv_action_content. applyStatusWriteToCache writes to
      // whichever id it actually landed on and invalidates every other id the
      // write cascaded to as a side effect.
      applyStatusWriteToCache({
        queryClient,
        data,
        requestedContentId: contentId,
        oldStatus: userStatus,
        contentType,
        username: user.username,
      });
    },
    onError: (error) => {
      // A multi-season series tile can't always tell which season a quick
      // queue/start should target (see resolve_tv_action_content) — that
      // refusal arrives as this specific error code, so it gets a tappable
      // toast that opens the show instead of the generic error toast.
      if (error.response?.data?.code === "ambiguous_season_target") {
        toast.error(error.response.data.message, {
          onClick: () => onClick?.(),
          autoClose: false,
        });
      } else {
        handleApiError(error);
      }
      setSubmitting(false);
    },
  });

  const deleteStatusMutation = useMutation({
    mutationFn: ({ contentId, confirm }) => deleteStatus(contentId, confirm),
    onSuccess: (data) => {
      setSubmitting(false);
      if (data.requireConfirmation) {
        setDeleteConfirmSeriesWide(!!data.seriesWide);
        setOpenConfirmDelete(true);
      } else {
        setShowStatusOverlay(false);
        setShowOptions(false);
        // A series-wide clear wipes every season's status and journal entries
        // server-side too — applyStatusClearToCache purges exactly what the
        // backend reports cleared rather than assuming it was only the tile's
        // own id, and invalidates whatever else it recomputed as a side effect.
        applyStatusClearToCache({
          queryClient,
          data,
          requestedContentId: contentId,
          contentType,
          username: user.username,
        });
        setOpenConfirmDelete(false);
      }
    },
    onError: (error) => {
      handleApiError(error);
      setSubmitting(false);
    },
  });

  // Tapping an already-active status triggers a delete (toggle off behaviour)
  const handlePosterActionClick = (status) => {
    if (!user) {
      openAuthDialog("Tracking content");
      return;
    }

    setSubmitting(true);
    if (status === userStatus) {
      deleteStatusMutation.mutate({
        contentId,
        confirm: false,
      });
    } else {
      addStatusMutation.mutate({
        contentId,
        status,
        createJournalEntry: true,
        fromShortcut: true,
      });
    }
  };

  // ─── Card Content ─────────────────────────────────────────────────────────
  const cardContent = (
    <>
      {skeleton || imageUrl === undefined ? (
        // Skeleton placeholder — static if skeleton prop is true, animated while image URL is loading
        <CardMedia
          component={Skeleton}
          variant="rectangular"
          width="100%"
          animation={skeleton ? false : "wave"}
          sx={{
            display: "block",
            paddingTop: "150%",
            height: 0,
            borderRadius: 1,
          }}
        />
      ) : imageUrl && !imageFailed ? (
        <>
          {/* Show animated skeleton until image has loaded */}
          {!imageLoaded && (
            <Skeleton
              variant="rectangular"
              animation="wave"
              sx={{
                display: "block",
                paddingTop: "150%",
                borderRadius: 1,
              }}
            />
          )}
          <CardMedia
            component="img"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageFailed(true)}
            sx={{
              display: imageLoaded ? "block" : "none",
              width: "100%",
              aspectRatio: "2/3",
              objectFit: "cover",
              objectPosition: "center",
              borderRadius: 1,
              // Highlight the poster border with the user's tracking status colour
              border:
                showStatus && userStatus
                  ? `3px solid ${theme.palette.status[userStatus]?.main}`
                  : "none",
            }}
            image={
              contentType === "book"
                ? `https://images.isbndb.com/covers/${imageUrl}.jpg`
                : `https://image.tmdb.org/t/p/w185/${imageUrl}`
            }
            alt={`${contentTitle} poster`}
          />
        </>
      ) : (
        // Fallback when no image URL is provided or image failed to load
        <Box sx={{ borderRadius: 1, overflow: "hidden", position: "relative" }}>
          <PlaceholderPoster title={contentTitle} overlay={overlay} />
          {/* Status border drawn over the placeholder rather than around it:
              a border on this wrapper would add to its 2:3 height, whereas the
              cover image's border sits inside its own 2:3 box — mismatched
              heights side by side in a row */}
          {showStatus && userStatus && (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: 1,
                border: `3px solid ${theme.palette.status[userStatus]?.main}`,
                pointerEvents: "none",
              }}
            />
          )}
        </Box>
      )}

      {/* Optional ranking position rendered over the top-left of the poster */}
      {position && (
        <Typography
          sx={{
            position: "absolute",
            top: 7,
            left: 10,
            color: "white",
            fontSize: { xs: "3rem", sm: "4rem" },
            lineHeight: 1,
            fontWeight: "bold",
            zIndex: 1, // Ensure it's above the image
            textShadow: `0 0 4px rgba(0,0,0,0.2)`,
          }}
        >
          {position}
        </Typography>
      )}

      {/* Optional content type icon in the same corner, styled to match the
          position number: white, with the same soft shadow for legibility.
          drop-shadow rather than textShadow, since the icon is an SVG. Sized
          (and inset) as a share of the card's own width — the card is a size
          container when showType is set — so it shrinks with the card rather
          than only stepping at breakpoints; 21px at desktop card sizes. */}
      {showType && !position && getContentOptions()[contentType] && (
        <Box
          sx={{
            position: "absolute",
            top: "clamp(3px, 3.5cqw, 6px)",
            // A touch more than top: the icons' glyphs sit tighter to their
            // box's left edge than its top, so equal insets read as off-centre
            left: "clamp(4px, 4.5cqw, 7.5px)",
            display: "flex",
            color: "white",
            zIndex: 1, // Ensure it's above the image
            filter: "drop-shadow(0 0 4px rgba(0,0,0,0.2))",
            "& svg": { fontSize: "clamp(12px, 12cqw, 21px)" },
          }}
        >
          {getContentOptions()[contentType].icon}
        </Box>
      )}
    </>
  );

  // Use Box for inline rendering (e.g. inside a dialog/carousel), Card otherwise
  const Wrapper = inline ? Box : Card;

  return (
    <>
      <ClickAwayListener
        onClickAway={() => {
          setShowOptions(false);
          setShowStatusOverlay(false);
        }}
      >
        <Wrapper
          // When custom touch handling is active, onClick is suppressed to avoid
          // synthetic click race conditions — all interactions go through touch events.
          // When not needed, native onClick handles everything directly.
          onClick={needsCustomTouchHandling ? undefined : onClick}
          onTouchStart={needsCustomTouchHandling ? handleTouchStart : undefined}
          onTouchEnd={needsCustomTouchHandling ? handleTouchEnd : undefined}
          onTouchMove={needsCustomTouchHandling ? handleTouchMove : undefined}
          // Prevent native long-press context menu and drag behaviour on touch devices,
          // regardless of whether custom touch handling is active.
          onContextMenu={isTouchDevice ? (e) => e.preventDefault() : undefined}
          // Prevent iOS from initiating a native element drag on long press now that
          // custom touch handlers are conditionally absent on some tiles.
          onDragStart={(e) => e.preventDefault()}
          // Children's animations bubble here too; only the card's own pop ends it
          onAnimationEnd={(e) => {
            if (e.animationName === "posterStatusPop") setStatusPop(false);
          }}
          onMouseEnter={() => {
            // Desktop only — touch devices synthesize mouseenter after tap sequences,
            // which would spuriously trigger overlays on newly rendered elements.
            if (isTouchDevice) return;
            if (listActions.length > 0) {
              setShowOptions(true);
            } else if (showStatus) {
              setShowStatusOverlay(true);
            }
          }}
          onMouseLeave={() => {
            if (isTouchDevice) return;
            setShowStatusOverlay(false);
            setShowOptions(false);
          }}
          sx={{
            maxWidth: 300,
            position: "relative",
            width: "100%",
            // Lets the type icon size itself to this card (cqw units)
            ...(showType && { containerType: "inline-size" }),
            padding: padding || undefined,
            boxShadow: padding ? "none" : "default",
            zIndex: zIndex || undefined,
            // Prevent text selection on long press
            userSelect: "none",
            // Tell Android's gesture system we're handling long-press ourselves, so it
            // doesn't enter a post-gesture blocked state that prevents subsequent long presses.
            // "manipulation" still allows panning/scroll — only disables double-tap zoom
            // and browser-native long-press recognition.
            touchAction: "manipulation",
            // One-shot "pop" when the long-press threshold fires, signalling the user
            // can lift to open. Transform-only (no layout shift); the slight overshoot
            // gives it a tactile feel and it settles back to rest while still held.
            "@keyframes posterLongPressPop": {
              "0%": { transform: "scale(1)" },
              "55%": { transform: "scale(1.03)" },
              "100%": { transform: "scale(1)" },
            },
            // A slightly bigger, slower pop confirming a status change
            "@keyframes posterStatusPop": {
              "0%": { transform: "scale(1)" },
              "40%": { transform: "scale(1.05)" },
              "100%": { transform: "scale(1)" },
            },
            animation: longPressArmed
              ? "posterLongPressPop 220ms cubic-bezier(0.34, 1.56, 0.64, 1)"
              : statusPop
                ? "posterStatusPop 380ms cubic-bezier(0.34, 1.56, 0.64, 1)"
                : "none",
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          {onClick ? (
            <CardActionArea>{cardContent}</CardActionArea>
          ) : (
            cardContent
          )}

          {/* ── List Actions Panel ───────────────────────────────────────────
            Shown on hover (desktop) or tap (touch) when listActions is non-empty.
            Hidden when the status overlay is open to avoid overlap.
            Always includes the built-in "Track" button as the last action. */}
          {listActions.length > 0 && showOptions && !showStatusOverlay && (
            <Stack
              direction="column"
              spacing="4px"
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              onTouchMove={(e) => e.stopPropagation()}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 2,
              }}
            >
              {secondaryActions.map((option) => (
                <ConditionalWrapper
                  key={option.value}
                  condition={!isTouchDevice}
                  wrapper={(children) => (
                    <Tooltip placement="left" title={option.label}>
                      {children}
                    </Tooltip>
                  )}
                >
                  <IconButton
                    // Touch: stopPropagation prevents bubbling to Wrapper,
                    //        preventDefault suppresses the synthetic click
                    // Click: stopPropagation prevents bubbling to Wrapper (desktop)
                    onTouchStart={(e) => {
                      e.stopPropagation();
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      // Drag-handle actions (e.g. Reorder) have no onClick —
                      // they're driven entirely by dnd-kit's attributes/listeners.
                      option.onClick?.(e);
                    }}
                    onTouchMove={(e) => {
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      option.onClick?.();
                    }}
                    aria-label={option.label}
                    size="small"
                    sx={{
                      p: "6px",
                      // The reorder handle is a dnd-kit drag activator, and dnd-kit's
                      // PointerSensor has no way to suppress WebKit's native panning —
                      // touch-action is the only thing holding the scroll back on iOS.
                      // It must therefore cover the whole tap target: declaring it on the
                      // icon alone leaves this button's 6px padding ring inheriting the
                      // Card's "manipulation", and iOS then scrolls the list mid-drag.
                      // Other actions keep "manipulation" so a scroll can still start on them.
                      touchAction:
                        option.value === "reorder" ? "none" : undefined,
                      background: (theme) =>
                        alpha(theme.palette.background.paper, 0.5), // Light transparent version
                      transition: "background 0.2s ease",
                      "&:hover": {
                        background: (theme) =>
                          alpha(theme.palette.background.paper, 1), // Less transparent on hover
                      },
                    }}
                  >
                    {option.icon}
                  </IconButton>
                </ConditionalWrapper>
              ))}
            </Stack>
          )}

          {/* ── Status Overlay ───────────────────────────────────────────────
            Shown on hover (desktop) or tap (touch) when showStatus is true.
            Displays a button for each tracking status. Tapping an active
            status toggles it off; tapping an inactive status sets it. */}
          {showStatusOverlay && (
            <Stack
              direction="column"
              spacing={0}
              sx={{
                position: "absolute",
                top: 3,
                right: 3,
                alignItems: "center",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                borderRadius: 1,
                padding: 0.5,
                zIndex: 2,
              }}
            >
              {submitting ? (
                <CircularProgress size="1rem" color="inherit" />
              ) : (
                statusOptions?.length > 0 &&
                statusOptions.map((option) => (
                  <ConditionalWrapper
                    key={option.value}
                    condition={!isTouchDevice}
                    wrapper={(children) => (
                      <Tooltip
                        placement="left"
                        title={
                          userStatus === option.value
                            ? option.label
                            : option.action
                        }
                      >
                        {children}
                      </Tooltip>
                    )}
                  >
                    <IconButton
                      // Touch: stopPropagation prevents bubbling to Wrapper,
                      //        preventDefault suppresses the synthetic click
                      // Click: stopPropagation prevents bubbling to Wrapper (desktop)
                      onTouchStart={(e) => {
                        e.stopPropagation();
                      }}
                      onTouchEnd={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handlePosterActionClick(option.value);
                      }}
                      onTouchMove={(e) => {
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePosterActionClick(option.value);
                      }}
                      sx={{ p: 0.5 }}
                    >
                      {userStatus === option.value
                        ? option.iconFilled
                        : option.icon}
                    </IconButton>
                  </ConditionalWrapper>
                ))
              )}
            </Stack>
          )}
        </Wrapper>
      </ClickAwayListener>

      {/* Confirmation dialog shown when clearing a status — always for a series-wide
          clear (which cascades to every season), otherwise only when journal
          entries are at stake */}
      <ConfirmActionDialog
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
        title={`Clear tracking for ${contentTitle}?`}
        message={
          <Typography>
            {deleteConfirmSeriesWide ? (
              "This removes tracking and journal entries for the whole series, including every season. This cannot be undone."
            ) : (
              <>
                {/* "season" rather than the generic TV label: reaching this
                    branch means the clear wasn't series-wide, and delete_status
                    only takes the non-series-wide path for a season (a series
                    with seasons always cascades). */}
                Removing tracking for this{" "}
                {contentType === "tvshow"
                  ? "season"
                  : getContentLabel({
                      type: contentType,
                      lowercase: true,
                    })}{" "}
                will also delete your journal entries for it. This cannot be
                undone.
              </>
            )}
          </Typography>
        }
        optionalAction={{
          label: "View entries",
          onClick: onClick,
        }}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={() =>
          deleteStatusMutation.mutate({
            contentId,
            confirm: true,
          })
        }
      />
    </>
  );
}

export default PosterTile;
